import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { translateValidationMessage } from '@/lib/i18n/validationMessages';

interface FormFieldProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}

export function FormField({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
  className,
}: FormFieldProps) {
  // The zod schemas are plain modules and cannot translate their own messages,
  // so it happens here — the one place a field error is ever rendered. An
  // unrecognised message passes through in English rather than becoming a raw
  // key.
  const t = useT();
  const shownError = translateValidationMessage(error, t);

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label
          htmlFor={htmlFor}
          className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]"
        >
          {label}
          {required && <span className="ml-1 text-[var(--color-accent-ink)]">*</span>}
        </label>
      )}
      {children}
      {shownError ? (
        <p className="text-xs text-[var(--color-error)]">{shownError}</p>
      ) : hint ? (
        <p className="text-xs text-[var(--color-fg-subtle)]">{hint}</p>
      ) : null}
    </div>
  );
}
